'use client';

import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef } from 'react';

/** Configuration constants for z-index ranges */
const GLOBAL_BASE_Z_INDEX = 1300;
const RANGE_SIZE = 20;
const CHILD_OFFSET = 5;

/**
 * Represents a registered popup in the stack
 */
export interface PopupStackEntry {
  /** Unique identifier for this popup */
  id: string;
  /** Start of the z-index range */
  baseIndex: number;
  /** Middle of the range - primary z-index for the popup */
  centerIndex: number;
  /** End of the z-index range */
  maxIndex: number;
  /** Callback to force-close this popup */
  onClose: () => void;
}

/**
 * Context value for managing popup z-index stack
 */
interface PopupStackContextValue {
  /**
   * Register a popup and get its z-index range
   * @param onClose - Callback to close this popup (used for cascading closes)
   * @returns Entry with z-index range information
   */
  register: (onClose: () => void) => PopupStackEntry;

  /**
   * Unregister a popup and release its z-index range
   * Also closes any child popups that were opened after this one
   * @param id - The popup's unique identifier
   */
  unregister: (id: string) => void;

  /**
   * Get a z-index for a child element relative to its parent popup
   * @param parentId - The parent popup's unique identifier
   * @param position - Whether child should be behind or in front of parent
   * @returns Computed z-index value
   */
  getChildZIndex: (parentId: string | undefined, position: 'behind' | 'front') => number;
}

const PopupStackContext = createContext<PopupStackContextValue | null>(null);

interface PopupStackProviderProps {
  children: ReactNode;
}

/**
 * Provider that manages z-index allocation for popups.
 *
 * Assigns z-index ranges (not single values) to each popup, allowing
 * child elements to position themselves within that range while
 * maintaining global stacking order.
 *
 * Z-Index Range Allocation Strategy:
 * - Global Base: 1300
 * - Range Size: 20 (±10 from center)
 * - Popup 1: 1300-1319 (center: 1310)
 * - Popup 2: 1320-1339 (center: 1330)
 * - etc.
 *
 * @example
 * ```tsx
 * // In your app root
 * <PopupStackProvider>
 *   <App />
 * </PopupStackProvider>
 *
 * // In a popup component
 * function MyPopup({ onClose }) {
 *   const { zIndex, getChildZIndex } = usePopupStack(onClose);
 *
 *   return (
 *     <Dialog style={{ zIndex }}>
 *       <SubPanel style={{ zIndex: getChildZIndex('behind') }} />
 *     </Dialog>
 *   );
 * }
 * ```
 */
export function PopupStackProvider({ children }: PopupStackProviderProps) {
  // Use refs to avoid re-renders when stack changes
  const popupsRef = useRef<Map<string, PopupStackEntry>>(new Map());
  const registrationOrderRef = useRef<string[]>([]);
  const nextIdRef = useRef(0);

  /**
   * Generate a unique ID for a popup
   */
  const generateId = useCallback((): string => {
    const id = `popup-${nextIdRef.current++}`;
    return id;
  }, []);

  /**
   * Calculate z-index range for a popup based on its position in the stack
   */
  const calculateRange = useCallback(
    (position: number): Pick<PopupStackEntry, 'baseIndex' | 'centerIndex' | 'maxIndex'> => {
      const baseIndex = GLOBAL_BASE_Z_INDEX + position * RANGE_SIZE;
      return {
        baseIndex,
        centerIndex: baseIndex + RANGE_SIZE / 2,
        maxIndex: baseIndex + RANGE_SIZE - 1,
      };
    },
    [],
  );

  /**
   * Register a new popup in the stack
   */
  const register = useCallback(
    (onClose: () => void): PopupStackEntry => {
      const id = generateId();
      const position = registrationOrderRef.current.length;
      const range = calculateRange(position);

      const entry: PopupStackEntry = {
        id,
        ...range,
        onClose,
      };

      popupsRef.current.set(id, entry);
      registrationOrderRef.current.push(id);

      return entry;
    },
    [generateId, calculateRange],
  );

  /**
   * Unregister a popup and close all child popups
   */
  const unregister = useCallback((id: string) => {
    const order = registrationOrderRef.current;
    const popupIndex = order.indexOf(id);

    if (popupIndex === -1) {
      return; // Already unregistered
    }

    // Get all popups registered after this one (children)
    const childIds = order.slice(popupIndex + 1);

    // Close children in reverse order (most recent first)
    for (let i = childIds.length - 1; i >= 0; i--) {
      const childId = childIds[i];
      const childEntry = popupsRef.current.get(childId);
      if (childEntry) {
        childEntry.onClose();
        popupsRef.current.delete(childId);
      }
    }

    // Remove this popup and all children from registration order
    registrationOrderRef.current = order.slice(0, popupIndex);
    popupsRef.current.delete(id);
  }, []);

  /**
   * Get z-index for a child element relative to parent popup
   */
  const getChildZIndex = useCallback(
    (parentId: string | undefined, position: 'behind' | 'front'): number => {
      if (!parentId) {
        // Fallback when no parent context
        const offset = position === 'behind' ? -CHILD_OFFSET : CHILD_OFFSET;
        return GLOBAL_BASE_Z_INDEX + RANGE_SIZE / 2 + offset;
      }

      const parentEntry = popupsRef.current.get(parentId);
      if (!parentEntry) {
        // Parent was unregistered, use fallback
        const offset = position === 'behind' ? -CHILD_OFFSET : CHILD_OFFSET;
        return GLOBAL_BASE_Z_INDEX + RANGE_SIZE / 2 + offset;
      }

      const offset = position === 'behind' ? -CHILD_OFFSET : CHILD_OFFSET;
      return parentEntry.centerIndex + offset;
    },
    [],
  );

  const value = useMemo<PopupStackContextValue>(
    () => ({
      register,
      unregister,
      getChildZIndex,
    }),
    [register, unregister, getChildZIndex],
  );

  return <PopupStackContext.Provider value={value}>{children}</PopupStackContext.Provider>;
}

/**
 * Hook to access the popup stack context
 * @throws Error if used outside of PopupStackProvider
 */
export function usePopupStackContext(): PopupStackContextValue {
  const context = useContext(PopupStackContext);
  if (!context) {
    throw new Error('usePopupStackContext must be used within a PopupStackProvider');
  }
  return context;
}

/**
 * Hook to access the popup stack context with fallback behavior
 * Returns null if context is not available (no provider)
 */
export function usePopupStackContextOptional(): PopupStackContextValue | null {
  return useContext(PopupStackContext);
}

export { CHILD_OFFSET, GLOBAL_BASE_Z_INDEX, RANGE_SIZE };
